import { Reveal, Label } from "../components/site/Reveal";
import { SITE } from "../config/site";

const sections = [
  {
    title: "Overview",
    body: [
      `This Privacy Policy explains what information ${SITE.name} ("NXTPlate", "we", "us") collects when you use our mobile app and website, how we use it, and the choices you have. NXTPlate is a wellness and nutrition app — it does not provide medical advice, medical diagnosis, or treatment.`,
    ],
  },
  {
    title: "Information we collect",
    list: [
      ["Account information", "When you create an account, we collect your email address and the sign-in credentials or identifiers needed to secure your account."],
      ["Nutrition and food logging data", "Meals, foods, portions, calories and macronutrients you log — whether entered manually, searched, or captured by photo — along with the dates and times of entries."],
      ["Weight, goals, and wellness information", "Details you choose to share such as weight, body goals, activity level, dietary preferences, and target calories or macros."],
      ["Photos uploaded for food analysis", "Photos of meals you take or upload so NXTPlate can estimate their nutritional content. Photos are processed to provide the photo-logging feature."],
      ["AI-generated recommendations", "Guidance and recommendations generated for you, and the inputs used to produce them, so we can maintain and improve the quality of that guidance."],
      ["Subscription information", "Your plan type, subscription status, and entitlement state. Payment transactions themselves are processed by Apple; we receive confirmation of your subscription status, not your payment card details."],
      ["Analytics and usage data", "How you interact with the app — screens visited, features used, crash and performance data — used to improve reliability and the product experience."],
      ["Device and technical data", "Device model, operating system version, app version, and similar technical identifiers."],
    ],
  },
  {
    title: "How we use your information",
    list: [
      ["Provide the core service", "Log your food, calculate remaining nutrition for the day, track progress, and generate next-meal guidance."],
      ["Personalize your experience", "Adapt targets, recommendations, and AI Coach guidance to your goals, history, and preferences."],
      ["Manage subscriptions", "Verify your plan and unlock the features included with it."],
      ["Improve NXTPlate", "Analyze aggregated usage and performance to fix issues and make the app better."],
      ["Communicate with you", "Respond to support requests and send important service messages."],
    ],
  },
  {
    title: "AI-generated recommendations",
    body: [
      "NXTPlate uses your logged meals, remaining daily nutrition, goals, and preferences to generate practical next-step recommendations and AI Coach guidance. These recommendations are generated for general wellness purposes only. They are informational, may not always be accurate, and are not medical advice, medical diagnosis, or treatment.",
    ],
  },
  {
    title: "Third-party service providers",
    body: [
      "We work with a small number of service providers to operate NXTPlate. They process data only as needed to provide their services to us:",
    ],
    list: [
      ["RevenueCat", "Manages subscription status and entitlements for in-app purchases. RevenueCat processes purchase and subscription data linked to an app user ID."],
      ["Apple", "Processes all in-app purchase payments and manages subscriptions through your Apple ID, under Apple's own terms and privacy policy."],
      ["Infrastructure and analytics providers", "Host the app and help us understand usage and performance. Where analytics are used, we aim to use aggregated or de-identified data."],
    ],
  },
  {
    title: "Data retention",
    body: [
      "We keep your account and logging data for as long as your account is active so the app can show your history and progress. If you delete your account, we delete or de-identify your personal data within a reasonable period, except where we must retain limited records for legal, security, or fraud-prevention purposes. Backup copies are removed in the ordinary course of backup rotation.",
    ],
  },
  {
    title: "Account deletion",
    body: [
      "You can delete your account at any time from within the app (Settings → Account → Delete Account) or by emailing us from the address on your account. Deletion removes your profile, food logs, uploaded photos, wellness information, and AI recommendation history, as described in this policy.",
    ],
  },
  {
    title: "Your rights",
    body: [
      "Depending on where you live, you may have the right to access, correct, export, or delete your personal information, and to object to or restrict certain processing. You can exercise any of these rights by contacting us at the email below. We will respond within a reasonable timeframe and in line with applicable law.",
    ],
  },
  {
    title: "Data security",
    body: [
      "We use reasonable technical and organizational measures to protect your information, including encrypted connections and access controls. No method of transmission or storage is completely secure, but we work to protect your data and to limit access to those who need it to operate the service.",
    ],
  },
  {
    title: "Children's privacy",
    body: [
      "NXTPlate is not directed at children under 13, and we do not knowingly collect personal information from them. If you believe a child has provided us personal information, contact us and we will delete it.",
    ],
  },
  {
    title: "Changes to this policy",
    body: [
      "We may update this Privacy Policy as the app evolves. If changes are significant, we will notify you in the app or by email. The \"last updated\" date above reflects the current version.",
    ],
  },
];

export default function Privacy() {
  return (
    <main className="bg-cream">
      <section className="noise relative overflow-hidden pt-32 md:pt-44 pb-12 md:pb-16">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Reveal><Label>Legal</Label></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl font-medium tracking-tight text-neutral-900 md:text-6xl">
              Privacy <span className="italic text-coral">Policy</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-4 text-sm font-medium uppercase tracking-[0.18em] text-neutral-400">
              Last updated: {SITE.legalLastUpdated}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="privacy-content" className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          {sections.map((s, i) => (
            <Reveal key={s.title} className="border-t border-neutral-200 py-10 first:border-t-0 first:pt-0">
              <div data-testid={`privacy-section-${i + 1}`}>
                <h2 className="font-display text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
                  <span className="mr-3 font-display text-base italic text-coral">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </h2>
                {s.body?.map((p, j) => (
                  <p key={j} className="mt-4 max-w-3xl text-base leading-relaxed text-neutral-600">{p}</p>
                ))}
                {s.list && (
                  <ul className="mt-6 space-y-4">
                    {s.list.map(([term, desc]) => (
                      <li key={term} className="max-w-3xl rounded-2xl bg-bone px-6 py-5">
                        <p className="text-sm font-semibold text-neutral-900">{term}</p>
                        <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{desc}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          ))}

          <Reveal className="border-t border-neutral-200 py-10">
            <div data-testid="privacy-contact">
              <h2 className="font-display text-2xl font-medium tracking-tight text-neutral-900 md:text-3xl">
                <span className="mr-3 font-display text-base italic text-coral">{String(sections.length + 1).padStart(2, "0")}</span>
                Contact us
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-neutral-600">
                Questions about this policy or your data? Email us at{" "}
                <a
                  href={`mailto:${SITE.supportEmail}`}
                  data-testid="privacy-email-link"
                  className="font-semibold text-neutral-900 underline decoration-coral decoration-2 underline-offset-4 transition-colors duration-200 hover:text-coral"
                >
                  {SITE.supportEmail}
                </a>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
